import { useState } from 'react';
import {
  AccountModel,
  CoQuanThue,
  DiaChi,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  NgoaiTe,
  NhanVien,
  PhuongThucGiaoHang,
  PhuongTienVanChuyen,
  QuyenChungTu
} from '@/types/schemas';

export interface FormFieldState {
  // Customer information
  khachHang: KhachHang | null;
  nhanVien: NhanVien | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  ngoaiTe: NgoaiTe | null;
  donViCoSo: DonViCoSo | null;

  // Other information
  diaChi: DiaChi | null;
  phuongTienVanChuyen: PhuongTienVanChuyen | null;
  phuongTienGiaoHang: PhuongThucGiaoHang | null;
  cucThue: KhachHang | null;
  coQuanThue: CoQuanThue | null;

  // So chung tu
  soChungTu: string;
  ty_gia: number;
  dienGiai: string;

  // Checkbox states
  hdbh_yn: boolean;
  px_yn: boolean;
  pt_tao_yn: boolean;
  ck_yn: boolean;
  loai_ck: string;

  // Payment method
  ma_httt: string;
}

export interface FormFieldActions {
  // Search field setters
  setKhachHang: (khachHang: KhachHang) => void;
  setNhanVien: (nhanVien: NhanVien) => void;
  setTaiKhoan: (taiKhoan: AccountModel) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setNgoaiTe: (ngoaiTe: NgoaiTe) => void;
  setDonViCoSo: (donViCoSo: DonViCoSo) => void;

  setDiaChi: (diaChi: DiaChi) => void;
  setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => void;
  setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => void;
  setCucThue: (cucThue: KhachHang) => void;
  setCoQuanThue: (coQuanThue: CoQuanThue) => void;

  // So chung tu
  setSoChungTu: (soChungTu: string) => void;
  setTyGia: (tyGia: number) => void;
  setDienGiai: (dienGiai: string) => void;

  // Checkbox setters
  setHdbhYn: (value: boolean) => void;
  setPxYn: (value: boolean) => void;
  setPtTaoYn: (value: boolean) => void;
  setCkYn: (value: boolean) => void;
  setLoaiCk: (value: string) => void;

  // Payment method setter
  setMaHttt: (value: string) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Customer information
  khachHang: null,
  nhanVien: null,
  taiKhoan: null,
  hanThanhToan: null,

  // Document information
  quyenChungTu: null,
  ngoaiTe: null,
  donViCoSo: null,

  // Other information
  diaChi: null,
  phuongTienVanChuyen: null,
  phuongTienGiaoHang: null,
  cucThue: null,
  coQuanThue: null,

  // So chung tu
  soChungTu: '',
  ty_gia: 1,
  dienGiai: '',

  // Checkbox states
  hdbh_yn: true,
  px_yn: true,
  pt_tao_yn: false,
  ck_yn: false,
  loai_ck: '2',

  // Payment method
  ma_httt: 'TMB'
};

function transformInitialData(initialData?: BanHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Customer information
    khachHang: initialData.ma_kh_data || null,
    nhanVien: initialData.ma_nv_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.han_tt_data || null,

    // Document information
    quyenChungTu: initialData.so_ct_data || null,
    ngoaiTe: initialData.ma_nt_data || null,
    donViCoSo: initialData.unit_id_data || null,

    // Other information
    diaChi: initialData.ma_dc_data || null,
    phuongTienVanChuyen: initialData.ma_ptvc_data || null,
    phuongTienGiaoHang: initialData.ma_ptgh_data || null,
    cucThue: initialData.ma_kh9_data || null,
    coQuanThue: initialData.ma_kh9_data || null,

    // So chung tu
    soChungTu: initialData.so_ct || '',
    ty_gia: initialData.ty_gia || 1,
    dienGiai: initialData.dien_giai || '',

    // Checkbox states
    hdbh_yn: initialData.hdbh_yn ?? true,
    px_yn: initialData.px_yn ?? true,
    pt_tao_yn: initialData.pt_tao_yn ?? false,
    ck_yn: initialData.ck_yn ?? false,
    loai_ck: initialData.loai_ck || '2',

    // Payment method
    ma_httt: initialData.ma_httt || 'TMB'
  };
}

export function useFormFieldState(initialData?: BanHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhanVien: (nhanVien: NhanVien) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setNgoaiTe: (ngoaiTe: NgoaiTe) => {
      setState(prev => ({
        ...prev,
        ngoaiTe
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setDiaChi: (diaChi: DiaChi) => {
      setState(prev => ({
        ...prev,
        diaChi
      }));
    },

    setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => {
      setState(prev => ({
        ...prev,
        phuongTienVanChuyen
      }));
    },

    setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => {
      setState(prev => ({
        ...prev,
        phuongTienGiaoHang
      }));
    },

    setCucThue: (cucThue: KhachHang) => {
      setState(prev => ({
        ...prev,
        cucThue
      }));
    },

    setCoQuanThue: (coQuanThue: CoQuanThue) => {
      setState(prev => ({
        ...prev,
        coQuanThue
      }));
    },

    // So chung tu
    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    setTyGia: (tyGia: number) => {
      setState(prev => ({ ...prev, ty_gia: tyGia }));
    },

    setDienGiai: (dienGiai: string) => {
      setState(prev => ({ ...prev, dienGiai }));
    },

    // Checkbox setters
    setHdbhYn: (value: boolean) => {
      setState(prev => ({ ...prev, hdbh_yn: value }));
    },

    setPxYn: (value: boolean) => {
      setState(prev => ({ ...prev, px_yn: value }));
    },

    setPtTaoYn: (value: boolean) => {
      setState(prev => ({ ...prev, pt_tao_yn: value }));
    },

    setCkYn: (value: boolean) => {
      setState(prev => ({ ...prev, ck_yn: value }));
    },

    setLoaiCk: (value: string) => {
      setState(prev => ({ ...prev, loai_ck: value }));
    },

    // Payment method setter
    setMaHttt: (value: string) => {
      setState(prev => ({ ...prev, ma_httt: value }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
